import { useMemo, useState } from 'react';
import { HiCog } from 'react-icons/hi';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi2';
import CustomTooltip from '../CustomTooltip';
import ColumnCustomizer from './ColumnCustomizer';
import { DataListHeaderProps } from './types';

/**
 * DataListHeader - Strong visual separation with sticky action column support
 * Header is sticky at top, action column is sticky on the right
 */
const DataListHeader = <T,>({
    columns,
    visibleColumns,
    onSort,
    sortConfig,
    className = '',
    stickyColumns = ['action'],
    onColumnOrderChange,
    onColumnVisibilityChange,
    onResetColumns,
    hiddenColumnKeys,
    fixedColumnKeys,
}: DataListHeaderProps<T>) => {
    const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
    const [isCustomizerOpen, setIsCustomizerOpen] = useState(false);
    
    // Separate visible columns into scrollable and sticky
    const { scrollableCols, stickyCols } = useMemo(() => {
        const visibleCols = columns.filter(col => visibleColumns[col.key]);
        const sticky = visibleCols.filter(col => stickyColumns.includes(col.key));
        const scrollable = visibleCols.filter(col => !stickyColumns.includes(col.key));
        return { scrollableCols: scrollable, stickyCols: sticky };
    }, [columns, visibleColumns, stickyColumns]);

    const handleSort = (key: string, sortable?: boolean) => {
        if (sortable === false) return;
        if (['members', 'action', 'created_by', 'team_members'].includes(key)) return;
        onSort?.(key);
    };

    const isSortable = (col: typeof columns[0]) => {
        if (col.sortable === false) return false;
        if (['members', 'action', 'created_by', 'team_members'].includes(col.key)) return false;
        return true;
    };

    const getSortIcon = (colKey: string, sortable: boolean) => {
        if (!sortable) return null;
        const isActive = sortConfig?.key === colKey;
        const isAsc = sortConfig?.direction === 'asc';

        return (
            <span className={`ml-1.5 inline-flex flex-shrink-0 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-70'} transition-opacity`}>
                {isActive && isAsc ? (
                    <HiChevronUp className="w-4 h-4 text-blue-400" />
                ) : isActive && !isAsc ? (
                    <HiChevronDown className="w-4 h-4 text-blue-400" />
                ) : (
                    <HiChevronDown className="w-4 h-4" />
                )}
            </span>
        );
    };

    // Render a header column
    const renderHeaderColumn = (col: typeof columns[0], isSticky: boolean = false) => {
        const sortable = isSortable(col);
        const isActive = sortConfig?.key === col.key;
        const isActionCol = col.key === 'action';

        return (
            <th
                key={col.key}
                className={`
                    group px-4 py-3 text-[11px] font-bold uppercase tracking-wider min-w-0 text-left align-middle
                    text-slate-700 dark:text-slate-200 z-[100]
                    select-none transition-colors duration-150
                    ${isActionCol ? 'overflow-visible' : 'overflow-hidden'}
                    ${!isActionCol && sortable ? 'cursor-pointer hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/70 dark:hover:bg-hover-dark' : ''}
                    ${isActive ? 'text-blue-600 dark:text-blue-400' : ''}
                    ${isSticky ? 'sticky right-0  bg-slate-100 dark:bg-surface-overlay' : ''}
                `}
                style={{ 
                    ...(isSticky ? { boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.08)' } : {})
                }}
                onClick={() => !isActionCol && handleSort(col.key, col.sortable)}
                onMouseEnter={() => setHoveredColumn(col.key)}
                onMouseLeave={() => setHoveredColumn(null)}
            >
                <div className={`flex items-center w-full min-w-0 ${isActionCol ? 'justify-center relative' : ''}`}>
                    {col.headerRender ? col.headerRender() : (
                        <CustomTooltip title={col.label} placement="top">
                            <span className="truncate">{col.label}</span>
                        </CustomTooltip>
                    )}
                    {!isActionCol && getSortIcon(col.key, sortable)}

                    {/* Settings button for Action column */}
                    {isActionCol && onColumnOrderChange && (
                        <>
                            <CustomTooltip title="Edit Columns" placement="top">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsCustomizerOpen(true);
                                    }}
                                    className="p-1.5 ml-1 text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-400 rounded-md hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
                                >
                                    <HiCog className="w-5 h-5" />
                                </button>
                            </CustomTooltip>
                            
                            {/* Column Customizer Modal */}
                            {isCustomizerOpen && (
                                <ColumnCustomizer
                                    isOpen={isCustomizerOpen}
                                    onClose={() => setIsCustomizerOpen(false)}
                                    columns={columns}
                                    visibleColumns={visibleColumns}
                                    onReorder={onColumnOrderChange!}
                                    onToggleVisibility={onColumnVisibilityChange!}
                                    onReset={onResetColumns!}
                                    hiddenColumnKeys={hiddenColumnKeys}
                                    fixedColumnKeys={fixedColumnKeys}
                                />
                            )}
                        </>
                    )}
                </div>
            </th>
        );
    };

    return (
        <thead
            className={`
                sticky top-0 z-30
                bg-slate-100 dark:bg-surface-overlay
                border-b-2 border-slate-200 dark:border-gray-500/60
                backdrop-blur-sm
                ${className}
            `}
        >
            <tr>
                {/* Scrollable header columns */}
                {scrollableCols.map(col => renderHeaderColumn(col, false))}

                {/* Sticky header columns */}
                {stickyCols.map(col => renderHeaderColumn(col, true))}
            </tr>
        </thead>
    );
};

export default DataListHeader;
